import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  CalendarHeart,
  Dna,
  Download,
  GitBranch,
  Home,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  PawPrint,
  Plus,
  Shield,
  StickyNote,
  UserCircle,
  UserPlus,
  Users,
  X,
  ArrowRightLeft
} from 'lucide-react';
import { ActionSheet, useBodyScrollLock, useEscapeKey } from './UI';
import { canAccessDna, canAccessProspects, hasAnyRole, useAuth } from '../context/AuthContext';

export interface NavTarget {
  to: string;
  label: string;
  icon: React.ReactNode;
  /** Als aktiv markieren, wenn die aktuelle URL mit einem dieser Pfade beginnt. */
  match: string[];
}

export const getPrimaryTargets = (t: TFunction): NavTarget[] => [
  { to: '/dashboard', label: t('nav:primary.cockpit'), icon: <LayoutDashboard size={20} />, match: ['/dashboard'] },
  { to: '/dashboard?tab=dogs', label: t('nav:primary.dogs'), icon: <PawPrint size={20} />, match: ['/dogs'] },
  { to: '/dashboard?tab=litters', label: t('nav:primary.litters'), icon: <CalendarHeart size={20} />, match: ['/breeding-plan'] },
  { to: '/account', label: t('nav:primary.account'), icon: <UserCircle size={20} />, match: ['/account'] }
];

export const isActive = (pathname: string, search: string, target: NavTarget) => {
  if (target.match.some((prefix) => pathname.startsWith(prefix))) return true;
  if (!target.to.startsWith('/dashboard') || pathname !== '/dashboard') return false;
  const targetTab = new URLSearchParams(target.to.split('?')[1] ?? '').get('tab') ?? 'overview';
  const currentTab = new URLSearchParams(search).get('tab') ?? 'overview';
  return targetTab === currentTab;
};

/**
 * isZuechterOrAdmin: Zuchtstaette anlegen nur fuer Zuechter/Admin. canAccessProspects: Interessent erfassen
 * zusaetzlich fuer alle Mitglieder einer Zuchtstaette (wie der Menuepunkt, siehe canAccessProspects).
 */
export const buildCreateActions = (navigate: NavigateFunction, t: TFunction, isZuechterOrAdmin: boolean, canAccessProspects: boolean) => [
  { label: t('nav:create.addDog'), icon: <PawPrint size={18} />, onClick: () => navigate('/dogs/new') },
  { label: t('nav:create.planLitter'), icon: <CalendarHeart size={18} />, onClick: () => navigate('/breeding-plan') },
  { label: t('nav:create.addNote'), icon: <StickyNote size={18} />, onClick: () => navigate('/notizen') },
  ...(canAccessProspects ? [{ label: t('nav:create.addProspect'), icon: <Users size={18} />, onClick: () => navigate('/interessenten') }] : []),
  ...(isZuechterOrAdmin ? [{ label: t('nav:create.createStation'), icon: <Home size={18} />, onClick: () => navigate('/breeding-station') }] : []),
  { label: t('nav:create.joinStation'), icon: <UserPlus size={18} />, onClick: () => navigate('/breeding-station/join') },
  { label: t('transfer:nav.takeOver'), icon: <ArrowRightLeft size={18} />, onClick: () => navigate('/hund-uebernehmen') }
];

/**
 * Feste Tab-Leiste am unteren Rand. Die wichtigsten Ziele liegen damit im
 * Daumenbereich statt oben rechts in der Kopfzeile.
 */
export const BottomNav: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);

  const primaryTargets = getPrimaryTargets(t);
  const [left, right] = [primaryTargets.slice(0, 2), primaryTargets.slice(2)];

  const renderItem = (target: NavTarget) => {
    const active = isActive(location.pathname, location.search, target);
    return (
      <Link
        key={target.to}
        to={target.to}
        className="bottom-nav-item"
        aria-current={active ? 'page' : undefined}
      >
        {target.icon}
        <span>{target.label}</span>
      </Link>
    );
  };

  return (
    <>
      <div className="bottom-nav-wrap md:hidden">
        <nav className="bottom-nav" aria-label={t('nav:aria.mainNav')}>
          {left.map(renderItem)}
          <div className="flex-1" />
          {right.map(renderItem)}
        </nav>

        {/* Bewusst ausserhalb von <nav>: die Welle in .bottom-nav wird per CSS-Mask
            ausgestanzt, und eine Mask stanzt auch alle Kind-Elemente mit aus - der
            Button wuerde sonst genau in der Ausbuchtung mitverschwinden. */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          aria-label={t('nav:aria.createNew')}
          className="fab-create absolute left-1/2 top-0 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-accent-500 text-white shadow-lg shadow-accent-900/50 transition-transform hover:bg-accent-600 active:scale-95"
        >
          <Plus size={26} />
        </button>
      </div>

      <ActionSheet
        isOpen={createOpen}
        title={t('nav:create.title')}
        onClose={() => setCreateOpen(false)}
        actions={buildCreateActions(navigate, t, hasAnyRole(user, ['Zuechter', 'Admin']), canAccessProspects(user))}
      />
    </>
  );
};

interface AppDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userEmail: string;
  stationName?: string;
  roles: string[];
  isAdmin: boolean;
  /** Ahnentafel nur anzeigen, wenn dafuer ausreichende Abstammungsdaten vorhanden sind. */
  hasPedigreeData: boolean;
  onLogout: () => void;
  /** Nur gesetzt, wenn die App noch nicht installiert ist */
  onInstall?: () => void;
}

/** Vollstaendiges Navigationsmenü inkl. Abmelden - erreichbar von jeder Seite aus. */
export const AppDrawer: React.FC<AppDrawerProps> = ({
  isOpen,
  onClose,
  userName,
  userEmail,
  stationName,
  roles,
  isAdmin,
  hasPedigreeData,
  onLogout,
  onInstall
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();

  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  // Beim Navigieren schliesst sich das Menü automatisch
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  if (!isOpen) return null;

  const isZuechter = roles.includes('Zuechter');
  const showProspects = canAccessProspects(user);

  // Enthaelt bewusst nur Ziele, die NICHT schon ueber die Bottom-Nav/Kopfzeile erreichbar sind
  // (primaryTargets) und NICHT schon ueber den "+"-Schnellzugriff (Hund/Wurf/Zuchtstaette anlegen).
  const links: NavTarget[] = [
    { to: '/notizen', label: t('nav:drawer.notes'), icon: <StickyNote size={20} />, match: ['/notizen'] },
    ...(showProspects ? [{ to: '/interessenten', label: t('nav:drawer.prospects'), icon: <Users size={20} />, match: ['/interessenten'] }] : []),
    ...(isZuechter || isAdmin ? [{ to: '/breeding-station', label: t('nav:drawer.manageStation'), icon: <Home size={20} />, match: ['/breeding-station'] }] : []),
    ...(isZuechter || isAdmin ? [{ to: '/uebergaben', label: t('transfer:nav.list'), icon: <ArrowRightLeft size={20} />, match: ['/uebergaben', '/hund-uebernehmen'] }] : []),
    ...(canAccessDna(user) ? [{ to: '/dna', label: t('nav:drawer.dna'), icon: <Dna size={20} />, match: ['/dna'] }] : []),
    ...(hasPedigreeData ? [{ to: '/pedigree', label: t('nav:drawer.pedigree'), icon: <GitBranch size={20} />, match: ['/pedigree'] }] : []),
    { to: '/support', label: t('nav:drawer.support'), icon: <LifeBuoy size={20} />, match: ['/support'] },
    ...(isAdmin ? [{ to: '/admin', label: t('nav:drawer.admin'), icon: <Shield size={20} />, match: ['/admin'] }] : [])
  ];

  // Portal an <body>, damit der Drawer nicht in einem Containing Block eines
  // Vorfahren mit backdrop-filter landet.
  return createPortal(
    <div className="fixed inset-0 z-[60] flex">
      <div className="anim-fade absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />

      <aside className="drawer-panel" role="dialog" aria-modal="true" aria-label={t('nav:aria.navigation')}>
        <div className="flex items-start justify-between gap-3 border-b border-dark-700 px-4 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="avatar-badge h-10 w-10 shrink-0 text-base">
              {(userName || userEmail || '?').charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-dark-50">{userName || userEmail}</div>
              {userEmail && userName !== userEmail && <div className="truncate text-xs text-dark-400">{userEmail}</div>}
              {stationName && (
                <div className="station-pill mt-2 inline-flex items-center">
                  <span className="truncate">{stationName}</span>
                </div>
              )}
            </div>
          </div>
          <button type="button" className="icon-btn -mr-2 shrink-0" onClick={onClose} aria-label={t('nav:aria.closeMenu')}>
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain p-2">
          {links.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="drawer-item"
              aria-current={isActive(location.pathname, location.search, link) ? 'page' : undefined}
            >
              <span className="shrink-0 text-dark-400">{link.icon}</span>
              <span className="truncate">{link.label}</span>
            </Link>
          ))}
        </nav>

        <div className="border-t border-dark-700 p-2">
          {onInstall && (
            <button type="button" className="drawer-item drawer-item-accent w-full" onClick={onInstall}>
              <Download size={20} className="shrink-0" />
              {t('nav:drawer.installApp')}
            </button>
          )}
          <button type="button" className="drawer-item drawer-item-danger w-full" onClick={onLogout}>
            <LogOut size={20} className="shrink-0" />
            {t('nav:drawer.logout')}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
};
